import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../ThemeContext';
import { Card, Button, Modal, Carousel, Badge } from 'react-bootstrap';
import { AlignCenter, Star, StarFill, Wallet2 } from 'react-bootstrap-icons';
import Lottie from 'lottie-react';
import scrollDownAnimation from '../assets/downButton.json';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useWebSocket } from './WebSocketContext';
import { FixedSizeGrid as Grid } from 'react-window';
import Codee from './ui/Codee';
import { useSalesFeedScrolling } from './ui/useSalesFeedScrolling';
import { useFilter } from './FilterContext';



const getColumnCountFor = (w) => {
  const width = Math.min(1504, w);
  if (width >= 1400) return [6, width / 6 - 8];
  if (width >= 1200) return [5, width / 5 - 8];
  if (width >= 992) return [4, width / 4 - 8];
  if (width >= 768) return [3, width / 3 - 8];
  if (width >= 576) return [3, width / 3 - 8];
  return [2, width / 2 - 8];
};
Math.max(500, window.innerWidth)
const SalesFeed = ({ sales = [], type, targetArticle, containerWidth }) => {
  const { showFeedbackOnly } = useFilter();
  const filteredSales = showFeedbackOnly
    ? sales.filter(sale => sale.feedbackpoints)
    : sales;
  const { theme } = useTheme();
  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';

  const { fetchMoreSales, fetchMoreFeedbackSales } = type === 'ws'
    ? useWebSocket()
    : { fetchMoreSales: null, fetchMoreFeedbackSales: null };

  // Локальный стейт для модалки:
  const [modalVisible, setModalVisible] = useState(false);
  const [modalImages, setModalImages] = useState([]);

  // Считаем начальный columnCount по умолчанию:
  const initialW = (type === 'main' && containerWidth) ? containerWidth : window.innerWidth;
  const [columnCount, setColumnCount] = useState(
    (type === 'ws' || type === 'yesterday' || type === 'main')
      ? getColumnCountFor(initialW)
      : [2, Math.min(500, initialW) / 2 - 30]
  );

  // Вешаем слушатель resize, чтобы пересчитывать columnCount
  const handleResize = useCallback(() => {
    const w = (type === 'main' && containerWidth) ? containerWidth : window.innerWidth;
    setColumnCount(
      (type === 'ws' || type === 'yesterday' || type === 'main')
        ? getColumnCountFor(w)
        : [2, Math.min(500, w) / 2 - 30]
    );
  }, [type, containerWidth]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize(); // первым делом посчитать
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (type === 'main') handleResize();
  }, [containerWidth, type, handleResize]);
  /**
   * Подключаем наш кастомный хук для работы со скроллом
   */
  const {
    listRef,
    unseenCount,
    handleScroll,
    scrollToNewestMessage,
    isAutoScrolling
  } = useSalesFeedScrolling({
    sales: filteredSales,
    fetchMoreSales: type === 'ws'
      ? (showFeedbackOnly ? fetchMoreFeedbackSales : fetchMoreSales)
      : null, // Передаем null для других типов
    type,
    targetArticle,
    columnCount,
    rowHeight: 430,
  });

  // Открытие/закрытие модалки с картинками
  const openModal = (images) => {
    setModalImages(images);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalImages([]);
  };

  // Слайдер картинок в модалке
  const renderImagesSlider = () => (
    <Modal show={modalVisible} onHide={closeModal} centered>
      <Modal.Body className={themeClass} style={{ borderRadius: '10px' }}>
        <Carousel interval={2000} controls indicators pause="hover" wrap>
          {modalImages.map((image, index) => (
            <Carousel.Item key={index}>
              <img className="d-block w-100" src={image.replace("c246x328", "c516x688")} alt={`Slide ${index}`} style={{ borderRadius: '10px' }} />
            </Carousel.Item>
          ))}
        </Carousel>
      </Modal.Body>
    </Modal>
  );

  // Декодирование HTML-сущностей
  const decodeHTMLEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Пример компонента со звёздочками рейтинга
  const RatingStars = ({ rating, feedbacks }) => {
    const stars = [1, 2, 3, 4, 5].map((star) =>
      rating < star
        ? <Star key={star} size={14} color="orange" style={{ marginTop: 1 }} />
        : <StarFill key={star} size={14} color="orange" style={{ marginTop: 1 }} />
    );

    return (
      <div
        style={{
          display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
          backgroundColor: '#fff', borderRadius: 10, height: 21,
          alignItems: 'center', position: 'absolute', top: -10, right: 0
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '3px' }}>
          {stars}
        </div>
        <span style={{ color: 'orange', marginRight: 5, fontSize: 12, fontWeight: '400' }}> | {feedbacks}</span>
      </div>
    );
  };

  // Пример компонента с временем
  const Time = ({ ts }) => {
    const date = new Date(ts * 1000);
    const hours = date.getHours();
    const minutes = ("0" + date.getMinutes()).slice(-2);
    const seconds = ("0" + date.getSeconds()).slice(-2);
    const formattedTime = `${hours}:${minutes}:${seconds}`;

    return <div className="time-style">{formattedTime}</div>;
  };

  const UnrefusableIcon = ({ theme }) => (
    <div style={{
      position: 'absolute',
      right: '2px',
      zIndex: 2,
      pointerEvents: 'none',
      animation: 'pulse 2s infinite'
    }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        style={{
          filter: 'drop-shadow(0 0 8px rgba(255, 68, 68, 0.5))',
          position: 'relative',
          fill: 'red'
        }}
        viewBox="0 0 19 18"
      >
        <path fill="inherit" fillRule="evenodd" d="M10.6 5.1a.9.9 0 0 0-.9.9v3.2a.9.9 0 1 0 1.8 0V6a.9.9 0 0 0-.9-.9Zm0 7.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" clipRule="evenodd" />
        <path fill="inherit" fillRule="evenodd" d="M10.072 2.18a6.703 6.703 0 0 0-3.653 1.455.974.974 0 0 1-.134.09c-.341.191-.774.163-1.057-.12-.322-.323-.327-.851.021-1.144a8.378 8.378 0 0 1 .342-.273A8.326 8.326 0 0 1 9.941.527a8.312 8.312 0 0 1 5.62 1.61 8.508 8.508 0 0 1 3.22 4.935 8.6 8.6 0 0 1-.737 5.869 8.43 8.43 0 0 1-4.339 3.96 8.289 8.289 0 0 1-5.838.132 8.415 8.415 0 0 1-4.51-3.758A8.584 8.584 0 0 1 2.27 8.067l-.856.744a.831.831 0 0 1-1.186-.037.858.858 0 0 1 .036-1.201l2.453-2.338a.833.833 0 0 1 1.223.079c.05.063.092.116.132.17.081.104.162.209.322.389l1.718 2.114a.858.858 0 0 1-.115 1.197.832.832 0 0 1-1.18-.116L3.931 7.98a6.917 6.917 0 0 0 .84 4.46 6.774 6.774 0 0 0 3.63 3.026 6.672 6.672 0 0 0 4.7-.107 6.785 6.785 0 0 0 3.492-3.187c.75-1.452.96-3.128.593-4.724a6.848 6.848 0 0 0-2.593-3.972 6.69 6.69 0 0 0-4.522-1.296Z" clipRule="evenodd" />
      </svg>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '140%',
        height: '140%',
        borderRadius: '50%',
        pointerEvents: 'none'
      }} />
    </div>
  );

  const Vigoda = ({ price, price2, feedbackpoints, feedbackpoints2 }) => {
    // Не показывать компонент если нет feedbackpoints2 или исходного feedbackpoints
    if (!feedbackpoints2) return null;

    const calculateBenefit = (fp, p) => Math.round((fp / p) * 100);
    const currentBenefit = feedbackpoints2 - price2;
    const currentPercentage = calculateBenefit(feedbackpoints2, price2);

    // Определяем цвет в зависимости от изменения выгоды
    let bgColor = '#0088c2'; // базовый цвет
    if (feedbackpoints2 > feedbackpoints) bgColor = '#28a745'; // зелёный
    else if (feedbackpoints2 < feedbackpoints) bgColor = '#ffc107'; // жёлтый

    // Старые расчеты для сравнения
    const previousBenefit = feedbackpoints - price;
    const previousPercentage = calculateBenefit(feedbackpoints, price);

    return (
      <div className="vigoda" style={{ backgroundColor: bgColor }}>
        {feedbackpoints2 !== feedbackpoints && (
          <span style={{ textDecoration: 'line-through', opacity: 0.7, marginRight: 4 }}>
            {previousBenefit} ({previousPercentage}%)
          </span>
        )}
        {currentBenefit} ({currentPercentage}%)
      </div>
    );
  };


  const AIValue = ({ ai, price, price2 }) => {

    // Оригинальная логика если нет feedbackpoints
    const aiInt = parseInt(ai, 10);
    const bgColor =
      price2 > price ? 'rgb(242, 73, 66)' :
        price2 < price ? 'rgb(52, 152, 219)' :
          'rgb(101, 211, 73)';

    return (
      <div className="ai-style" style={{ backgroundColor: bgColor }}>
        AI: {aiInt}%
      </div>
    );
  };

  function Diapaz(diapazmin, diapazmax) {
    let mind = diapazmin == null ? 0 : diapazmin.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumSignificantDigits: 21 });
    let maxd = diapazmax == null ? 0 : diapazmax.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumSignificantDigits: 21 });
    let diapaz = `${mind} - ${maxd}`;
    if (mind === 0 && maxd === 0) {
      diapaz = null;
    }
    return diapaz;
  }

  const MarketType = ({ marketplace }) => {
    let marketColor;
    if (marketplace === 'wb') marketColor = '#8A2BE2';
    else if (marketplace === 'ozon') marketColor = '#007FFF';
    else marketColor = "#00C2FC";

    return (
      <div
        style={{
          display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
          backgroundColor: marketColor, borderRadius: 10, height: 21,
          alignItems: 'center', position: 'absolute', top: -10, left: 0
        }}
      >
        <span style={{ color: 'white', marginLeft: 5, marginRight: 5, fontSize: 12, fontWeight: '600' }}>
          {marketplace.toUpperCase()}
        </span>
      </div>
    );
  };

  const FeedbackpointsMod = ({ feedbackpoints, feedbackpoints2 }) => {
    if (!feedbackpoints) return null;

    // Определяем статус изменения
    let status = 'same';
    if (!feedbackpoints2) status = 'removed';
    else if (feedbackpoints2 > feedbackpoints) status = 'increased';
    else if (feedbackpoints2 < feedbackpoints) status = 'decreased';

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: status === 'removed' ? '#dc3545' : '#0088c2',
        borderRadius: 10,
        height: 23,
        position: 'absolute',
        top: 12,
        left: 0,
        padding: '0 5px'
      }}>
        <Wallet2 size={14} color="white" style={{ marginRight: 4 }} />

        {/* Удалённый случай */}
        {status === 'removed' && (
          <span style={{
            color: 'white',
            textDecoration: 'line-through',
            fontSize: 15,
            fontWeight: '600'
          }}>
            {feedbackpoints}₽
          </span>
        )}

        {/* Изменённые случаи */}
        {feedbackpoints2 !== feedbackpoints && feedbackpoints2 && (
          <>
            <span style={{
              textDecoration: 'line-through',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 12,
              marginRight: 4
            }}>
              +{feedbackpoints}₽
            </span>
            <span style={{
              color: status === 'increased' ? '#a5ffba' :
                status === 'decreased' ? '#ffde79' : 'white',
              fontSize: 15,
              fontWeight: '600'
            }}>
              +{feedbackpoints2}₽
            </span>
          </>
        )}

        {/* Без изменений */}
        {status === 'same' && (
          <span style={{ color: 'white', fontSize: 15, fontWeight: '600' }}>
            +{feedbackpoints}₽
          </span>
        )}
      </div>
    );
  };

  const MarketTypeButton = ({ marketplace, article, seller }) => {
    if (marketplace === 'wb') {
      if (seller) {
        return (
          <div style={{ display: 'flex', width: '100%' }}>
            <Button
              style={{
                backgroundColor: '#8A2BE2',
                border: 'none',
                flex: 1,
                marginRight: '8px' // небольшой отступ справа
              }}
              href={`https://www.wildberries.ru/catalog/${article}/detail.aspx`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Перейти
            </Button>
            <Button
              style={{
                backgroundColor: 'red',
                border: 'none',
                flex: 1
              }}
              href={seller}
              target="_blank"
              rel="noopener noreferrer"
            >
              Прод
            </Button>
          </div>
        );
      } else {
        return (
          <Button
            style={{ backgroundColor: '#8A2BE2', border: 'none' }}
            href={`https://www.wildberries.ru/catalog/${article}/detail.aspx`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Перейти
          </Button>
        );
      }
    } else if (marketplace === 'ozon') {
      return (
        <Button
          style={{ backgroundColor: '#007FFF', border: 'none' }}
          href={`https://ozon.ru/products/${article}/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Перейти
        </Button>
      );
    } else {
      return (
        <Button
          style={{ backgroundColor: "#00C2FC", border: 'none' }}
          href={`https://www.detmir.ru/product/index/id/${article}/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Перейти
        </Button>
      );
    }
  };


  const SkaldObr = ({ sklad, skladR }) => {
    const cleanString = (str) => {
      if (typeof str !== 'string') return 'un';
      return str
        .replace(/<b>/g, '')
        .replace(/<\/b>/g, '')
        .replace(/😍 /g, '')
        .replace(/⚠️ /g, '')
        .replace(/шт\./g, '')
        .replace(/WB - /g, '')
        .replace(/ПРОД - /g, '');
    };

    if (!skladR || skladR.trim() === '') {
      skladR = '0';
    }

    let chek_sklad = sklad ? sklad.split(' | ') : [];
    let chek_sklad2 = skladR.split(' | ');
    let formattedSklad1 = '';
    let formattedSklad2 = '';

    if (chek_sklad2.length > 1) {
      formattedSklad1 = `${cleanString(chek_sklad2[0])}/${cleanString(chek_sklad[0])}`;
      formattedSklad2 = `${cleanString(chek_sklad2[1])}/${cleanString(chek_sklad[1])}`;
    } else {
      formattedSklad1 = `${cleanString(chek_sklad2[0])}/${cleanString(chek_sklad[0])}`;
    }

    const getBadgeColor = (str) => {
      if (str && str.includes('WB - ')) return 'green';
      else if (str && str.includes('ПРОД - ')) return 'orange';
      return '';
    };

    return (
      <>
        {chek_sklad.length > 1 ? (
          <div className="stock-container">
            <p className={`stock-badge ${getBadgeColor(chek_sklad[0])}`}>
              {formattedSklad1}
            </p>
            <p className={`stock-badge ${getBadgeColor(chek_sklad[1])}`}>
              {formattedSklad2}
            </p>
          </div>
        ) : (
          <p className={`single-stock-badge ${getBadgeColor(chek_sklad[0])}`}>
            {formattedSklad1}
          </p>
        )}
      </>
    );
  };
  const getPhotos = (linkphoto) =>
    typeof linkphoto === 'string' && linkphoto.trim()
      ? linkphoto.split(',')
      : [];

  const parseSizes = (sizesString) => {
    if (!sizesString || typeof sizesString !== 'string') return [];
    return sizesString.split('|').map((item) => {
      const trimmed = item.trim();
      const isW = trimmed.startsWith('(W)');
      const isP = trimmed.startsWith('(P)');
      const color = isW ? 'green' : isP ? 'orange' : 'gray';
      const text = trimmed.replace('(W)', '').replace('(P)', '').trim();
      return { color, text };
    });
  };
  console.log()
  // Компонент (ячейка) для одной карточки товара
  const Cell = React.memo(({ index, style, sale, themeClass, openModal, columnCount }) => {
    const sizesData = parseSizes(sale.sizes);
    const photos = getPhotos(sale.linkphoto);
    const fallback = '/no-photo.webp';
    return (
      <div
        key={sale.id}
        className="card-container"
        data-index={index}
        style={{ ...style, padding: '10px 0px 10px 15px' }}
      >
        <Card
          className={`mb-4 ${themeClass} sale-card`}
          style={{
            opacity: sale.col2 < 1 && sale.marketplace === 'wb' && type !== 'yesterday' && type !== 'main' ? '0.5' : '1',
            outline: sale.feedbackpoints ? '1px solid rgba(0, 136, 194, 1)' : 'none',
            outlineOffset: '2px',
            boxShadow: sale.feedbackpoints ? '0 0 32px rgba(0, 136, 194, 3.2)' : 'none',
            transition: 'outline 0.3s ease, box-shadow 0.3s ease',
          }}
        >
          <div
            className="image-container"
            onClick={() => photos.length && openModal(photos)}
          >
            <Card.Img
              variant="top"
              loading="lazy"
              src={photos[0] || fallback}
              className="sale-image"
              onError={(e) => { e.currentTarget.src = fallback; }}
            />
            <Time ts={sale.ts} />
            {sale.isunrefusable && <UnrefusableIcon />}
            {sale.feedbackpoints && <Vigoda price={sale.price} price2={sale.price2} feedbackpoints={sale.feedbackpoints} feedbackpoints2={sale.feedbackpoints2} />}
            {sale.marketplace === 'wb' && sale.ai >= 0 && (
              <AIValue ai={sale.ai} price={sale.price} price2={sale.price2} />
            )}
            {sale.sklad && <SkaldObr sklad={sale.sklad} skladR={sale.sklad2} />}

            {sale.col2 !== 0 && sizesData.length > 0 && (
              <div
                className="sizes-scroll"
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '2px',
                  padding: '2px'
                }}
              >
                {sizesData.map((item, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor:
                        item.color === 'green'
                          ? 'rgba(0,128,0,0.5)'
                          : item.color === 'orange'
                            ? 'rgba(255,165,0,0.5)'
                            : 'rgba(102,102,102,0.5)',
                      color: '#fff',
                      padding: '1px 3px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.text}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="sale-content">
            <div className="price-container">
              <p
                className="price-text"
                style={{
                  backgroundColor:
                    sale.marketplace === 'wb'
                      ? sale.price2 > sale.price
                        ? '#f24942'
                        : sale.price2 < sale.price
                          ? '#3498db'
                          : '#65d349'
                      : '#65d349'
                }}
              >
                {sale.marketplace === 'wb' && sale.price2
                  ? sale.price2.toLocaleString('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                    maximumSignificantDigits: 21
                  })
                  : sale.price.toLocaleString('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                    maximumSignificantDigits: 21
                  })}
              </p>
              <div className="price-details">
                <p
                  className={`diapaz-text ${Diapaz(sale.diapazmin, sale.diapazmax) == null
                    ? 'no-diapaz'
                    : ''
                    }`}
                >
                  {Diapaz(sale.diapazmin, sale.diapazmax) == null
                    ? 'Нет диапазона'
                    : Diapaz(sale.diapazmin, sale.diapazmax)}
                </p>
                <>
                  <div className="price-row">
                    <p style={{ marginBottom: 0 }}>
                      {sale.marketplace === 'wb' && sale.price2 > sale.price
                        ? '📈'
                        : sale.marketplace === 'wb' && sale.price2 < sale.price
                          ? '📉'
                          : ''}
                    </p>
                    <s className="s-price">
                      {sale.marketplace === 'wb' &&
                        sale.price2 &&
                        sale.price !== sale.price2
                        ? sale.price.toLocaleString('ru-RU', {
                          style: 'currency',
                          currency: 'RUB',
                          maximumSignificantDigits: 21
                        })
                        : ''}
                    </s>
                  </div>
                </>
              </div>
            </div>

            <p className={`sale-name ${themeClass}`}>
              {decodeHTMLEntities(sale.name)}
            </p>
            <MarketType marketplace={sale.marketplace} />
            <FeedbackpointsMod feedbackpoints={sale.feedbackpoints} feedbackpoints2={sale.feedbackpoints2} />

            {sale.marketplace === 'wb' && (
              <RatingStars rating={sale.rating} feedbacks={sale.feedbacks} />
            )}
            {sale.subject && (
              <div className="subject-style">
                {sale.subject.split(/(<code>.*?<\/code>)/g).map((part, index) =>
                  part.includes('<code>') ? (
                    <Codee>
                      {part.replace(/<\/?code>/g, '')}
                    </Codee>
                  ) : (
                    <span key={index}>
                      {part.length > 40 ? part.slice(0, 40) + '...' : part}
                    </span>
                  )
                )}
              </div>
            )}
            <MarketTypeButton marketplace={sale.marketplace} article={sale.article} seller={sale.seller} />
          </div>
        </Card>
      </div>
    );
  });

  // cellRenderer из react-window
  const cellRenderer = useCallback(
    ({ columnIndex, rowIndex, style, data }) => {
      const { sales, themeClass, openModal, columnCount } = data;
      const index = rowIndex * columnCount[0] + columnIndex;
      if (index >= sales.length) return null;

      const sale = sales[index];
      return (
        <Cell
          index={index}
          style={style}
          sale={sale}
          themeClass={themeClass}
          openModal={openModal}
          columnCount={columnCount}
        />
      );
    },
    []
  );

  // Стили для Grid
  const typeStyle = () => {
    if (type === 'yesterday') {
      return {
        overflowY: 'auto',
        height: 'calc(100vh - 133px)',
        width: '100%',
        overflowX: 'hidden',
      };
    }
    if (type === 'modal') {
      return {
        overflowY: 'unset',
        overflowX: 'unset',
        height: 'auto',
        width: '80vh !important'
      };
    }
    if (type === 'main') {
      return { overflowY: 'auto', height: 560, width: '100%', overflowX: 'hidden' }; // компактный блок на главной
    }
    if (type === 'ws') {
      return {
        overflowY: 'auto',
        height: 'auto',
        width: '80vh !important'
      };
    }
    return {
      overflowY: 'auto',
      height: 'auto',
      width: '80vh !important'
    };
  };



  return (
    <>
      {filteredSales.length > 0 ? (<Grid
        columnCount={
          type === 'yesterday' || type === 'ws' || type === 'main'
            ? columnCount[0]
            : filteredSales.length === 1
              ? 1
              : columnCount[0]
        }
        columnWidth={columnCount[1]}
        height={
          type === 'yesterday' ? window.innerHeight - 200
            : type === 'main' ? 560
              : window.innerHeight - 60
        }
        rowCount={Math.ceil(filteredSales.length / columnCount[0])}
        rowHeight={430}
        width={
          type === 'yesterday' ? window.innerWidth
            : type === 'ws' ? window.innerWidth
              : type === 'main' ? (containerWidth || Math.min(1504, window.innerWidth))
                : window.innerWidth * 0.8
        }
        itemData={{ sales: filteredSales, themeClass, openModal, columnCount }}
        children={cellRenderer}
        onScroll={type === 'ws' ? handleScroll : ''}
        ref={listRef}
        className={type === 'ws' || type === 'yesterday' ? 'scrollable' : ''}
        overscanRowCount={type === 'ws' ? 3 : 20}
        overscanColumnCount={type === 'ws' ? 2 : 20}
        style={typeStyle()}
      />) : (
        <div className="no-results">
          Ничего не найдено по выбранному фильтру. 😕
        </div>
      )}

      {renderImagesSlider()}

      {!isAutoScrolling && type === 'ws' && (
        <div className="scroll-to-bottom-container">
          {unseenCount > 0 && (
            <Badge pill bg="primary" className="unseen-count-badge">
              {unseenCount}
            </Badge>
          )}

          <div className="scroll-to-bottom-button" onClick={scrollToNewestMessage}>
            <Lottie
              animationData={scrollDownAnimation}
              loop={false}
              initialSegment={[0, 70]}
              speed={2}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SalesFeed;


